import { useSEO } from '../hooks/useSEO';
import styles from './About.module.css';
import { SectionWrapper } from '../components/shared/SectionWrapper';
import { Chip } from '../components/shared/Chip';
import { ScriptText } from '../components/shared/ScriptText';
import { StoreBadges } from '../components/shared/StoreBadges';
import { GrainOverlay } from '../components/shared/GrainOverlay';

const stats = [
  { n: '500k+', l: 'couples playing' },
  { n: '12M', l: 'matched items' },
  { n: '94%', l: 'say it helped' },
  { n: '0', l: 'data sold. ever.' },
];

const values = [
  { t: 'Two yeses, never one.', d: 'Every shared answer requires both partners to say yes. Nothing appears unless it\'s mutual.' },
  { t: 'Private by design.', d: 'Answers are encrypted on-device. We never see them, store them, or sell them.' },
  { t: 'Made for play, not performance.', d: 'No scores, no leaderboards, no judgment. Just prompts that spark real conversation.' },
  { t: 'Sex-positive, every body.', d: 'Inclusive by default. Every orientation, every body, every comfort level is welcome.' },
];

const team = [
  { name: 'Asha Kapoor', role: 'CEO & Co-founder', note: 'Former therapist turned tech. Believes intimacy is a skill, not a mystery.', hue: '#FF2E7E' },
  { name: 'Ravi Menon', role: 'CTO & Co-founder', note: 'Privacy-first engineer. Built encryption systems at Signal before joining.', hue: '#a855f7' },
  { name: 'Dr. Lena Osei', role: 'Head of Research', note: 'Relationship psychologist. Designs every question to surface, not pressure.', hue: '#EC4899' },
  { name: 'Tomás Rivera', role: 'Lead Designer', note: 'Believes design should feel like foreplay — anticipation is everything.', hue: '#F472B6' },
  { name: 'Yuki Tanaka', role: 'Head of Content', note: 'Writes the stories, dares, and prompts. Formerly at Esther Perel\'s studio.', hue: '#DB2777' },
  { name: 'Sam Chen', role: 'Growth Lead', note: 'Grew 3 consumer apps to 1M+ users. Obsessed with word-of-mouth.', hue: '#9D174D' },
];

const press = [
  { src: 'Cosmopolitan', quote: '"The app that finally gets couple games right — tasteful, playful, and surprisingly deep."' },
  { src: 'WIRED', quote: '"Privacy-first intimacy tech that proves you don\'t need to sell data to build a great product."' },
  { src: 'Vogue', quote: '"If you\'re looking for a date night upgrade, LuvLust is the app therapists actually recommend."' },
];

export function About() {
  useSEO({
    title: 'About LuvLust — Our Mission & Team',
    description: 'LuvLust was born from a belief that every couple deserves a spark. Meet the team building the #1 couples app.',
    keywords: 'about luvlust, couples app team, relationship app mission',
  });

  return (
    <>
      {/* Hero */}
      <SectionWrapper className={styles.hero}>
        <GrainOverlay />
        <div className={styles.heroContent}>
          <Chip>About LuvLust</Chip>
          <h1 className={`display ${styles.h1}`}>
            We believe every couple<br />deserves a <ScriptText rotate={-3}>spark.</ScriptText>
          </h1>
          <p className={styles.heroSub}>
            LuvLust was born from a simple idea: what if an app could help couples rediscover
            the playfulness, curiosity, and vulnerability that made them fall in love in the first place?
          </p>
        </div>
      </SectionWrapper>

      {/* Mission */}
      <SectionWrapper className={styles.mission}>
        <div className={styles.missionGrid}>
          <div>
            <Chip>Our Mission</Chip>
            <h2 className={`display ${styles.h2}`}>
              Make intimacy<br />a <ScriptText rotate={-2}>practice,</ScriptText><br />not a mystery.
            </h2>
            <p className={styles.body}>
              Relationships thrive on intention. We build tools that turn "we should talk about that"
              into "let's play." Every feature — from dares to quizzes to stories — is designed to
              lower the barrier to vulnerability and raise the ceiling on connection.
            </p>
            <p className={styles.body}>
              We don't believe in fixing relationships. We believe in fueling them.
            </p>
          </div>
          <div className={styles.missionImage}>
            <div className={styles.missionOverlay}>
              <ScriptText className={styles.missionScript}>made for two.</ScriptText>
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* Stats */}
      <SectionWrapper>
        <div className={styles.statsBox}>
          {stats.map((s) => (
            <div key={s.l} className={styles.stat}>
              <div className={styles.statValue}>{s.n}</div>
              <div className={styles.statLabel}>{s.l}</div>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* Values */}
      <SectionWrapper>
        <div className={styles.valuesHeader}>
          <Chip>Our Values</Chip>
          <h2 className={`display ${styles.h2}`}>What we <ScriptText rotate={-2}>stand for.</ScriptText></h2>
        </div>
        <div className={styles.valuesGrid}>
          {values.map((v, i) => (
            <div key={v.t} className={styles.valueCard}>
              <span className={styles.valueTag}>{String(i + 1).padStart(2, '0')}</span>
              <h3 className={styles.valueTitle}>{v.t}</h3>
              <p className={styles.valueDesc}>{v.d}</p>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* Story */}
      <SectionWrapper className={styles.story}>
        <Chip>Our story</Chip>
        <h2 className={`display ${styles.h2}`} style={{ marginTop: 18 }}>
          It started with a <ScriptText rotate={-2}>quiz.</ScriptText>
        </h2>
        <div className={styles.storyText}>
          <p>Asha and Ravi had been together for eight years when they realized they'd stopped surprising each other. Not because they didn't care — but because they'd run out of ways to ask.</p>
          <p>One evening, Asha wrote five questions on index cards. "Would you want to try a couples massage?" "Have you ever wanted to write me a love letter?" Simple stuff. They answered privately, then revealed only the ones where both said yes.</p>
          <p>Three matched yeses later, they had their best date night in years. LuvLust was born that night — built on the belief that what couples need isn't therapy. It's play.</p>
        </div>
      </SectionWrapper>

      {/* Team */}
      <SectionWrapper>
        <div className={styles.teamHeader}>
          <Chip>The Team</Chip>
          <h2 className={`display ${styles.h2}`}>Built by people who <ScriptText rotate={-2}>care.</ScriptText></h2>
        </div>
        <div className={styles.teamGrid}>
          {team.map((m) => (
            <div key={m.name} className={styles.teamCard} style={{ background: `linear-gradient(180deg, ${m.hue}18, rgba(20,5,10,0.6))` }}>
              <div className={styles.avatar} style={{ background: `radial-gradient(circle, ${m.hue}55, ${m.hue}22)` }} />
              <h3 className={styles.memberName}>{m.name}</h3>
              <p className={styles.memberRole}>{m.role}</p>
              <p className={styles.memberNote}>{m.note}</p>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* Press */}
      <SectionWrapper id="press">
        <div className={styles.pressHeader}>
          <Chip>In the Press</Chip>
          <h2 className={`display ${styles.h2}`}>What they're <ScriptText rotate={-2}>saying.</ScriptText></h2>
        </div>
        <div className={styles.pressGrid}>
          {press.map((p) => (
            <div key={p.src} className={styles.pressCard}>
              <div className={styles.pressSrc}>{p.src}</div>
              <p className={styles.pressQuote}>{p.quote}</p>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* CTA */}
      <SectionWrapper className={styles.cta}>
        <GrainOverlay />
        <div className={styles.ctaInner}>
          <h2 className={`display ${styles.ctaTitle}`}>
            Come <ScriptText rotate={-2}>play</ScriptText> with us.
          </h2>
          <StoreBadges analyticsLocation="about" />
        </div>
      </SectionWrapper>
    </>
  );
}
